import { Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { HelmetProvider } from "react-helmet-async";
import { i18nInit } from "./i18n";
import { PrivateRoutes, PublicRoutes } from "Routes";
import { AuthLayout, PrivateLayout, PublicLayout } from "Layout";
import ErrorBoundary from "./Components/ErrorBoundary";
import { AuthProvider } from "./Context/AuthContext";
import { EmailBuilderProvider } from "./Context/EmailBuilderContext";
import { paths } from "./Constant/paths";
import { LoadingScreen } from "./Components";
import {
  AboutUs,
  CareerDetails,
  CareerJobsList,
  Careers,
  ContactUs,
  DataSecurity,
  InternshipManagementSystem,
  Home,
  Jobs,
  PrivacyPolicy,
} from "Views/Public";
import {
  CreateAccount,
  ForgetPassword,
  Login,
  ResetPassword,
  VerifyEmail,
} from "./Views/Auth";
import Onboarding from "./Views/Private/Onboarding";
import SearchResult from "./Views/Private/SearchResult";
import AccountSetting from "./Views/Private/AccountSetting";
import InviteMember from "./Views/Private/InviteMember";
import CareerHub from "Views/Private/CareerHub";
import Analytics from "./Views/Private/Analytics";
import ViewProfile from "Views/Private/ViewProfile";
import PlutoConnect from "Views/Private/PlutoConnect";
import {
  Action,
  AdminSettings,
  Attributes,
  Field,
  Groups,
  Roles,
  Stages,
} from "./Views/Private/Admin/AdminSettings";
import { AttributeDetails } from "./Views/Private/Admin/AdminSettings/Attributes/AttributeDetails";
import AdminDashboard from "./Views/Private/Admin/Dashboard";
import ApplicationStatus from "./Views/Private/Admin/ApplicationStatus";
import { ApplicantDetails } from "./Views/Private/Admin/ApplicantDetails";
import AdminProjects from "./Views/Private/Admin/Projects";
import MasterData from "Views/Private/Admin/MasterData";
import { MasterFieldDetails } from "Views/Private/Admin/MasterData/MasterFieldDetails";
import AdminEmail from "./Views/Private/Admin/Email";
import EmailBuilder from "./Views/Private/Admin/EmailBuilder";
import Recommendation from "./Views/Private/Admin/Recommendation";
import Events from "./Views/Private/Admin/Events";
import { CreateEvent } from "./Views/Private/Admin/Events/CreateEvent";
import { PreviewEvent } from "./Views/Private/Admin/Events/PreviewEvent";
import OfferLetter from "Views/Private/Admin/OfferLetter";
import { NewOfferLetter } from "Views/Private/Admin/OfferLetter/NewOfferLetter";
import {
  CustomizeForm,
  FormDesign,
  FormSettings,
} from "./Views/Private/FormBuilder/CustomizeForm";
import FormInstruction from "./Views/Public/FormInstruction";
import GuestApplication from "./Views/Public/GuestApplication";
import StudentDashboard from "./Views/Private/Student/StudentDashboard";
import StudentEmail from "./Views/Private/Student/StudentEmail";
import FormBuilderDashboard from "./Views/Private/FormBuilder/FormBuilderDashboard/FormBuilderDashboard";
import FormApplication from "./Views/Private/FormBuilder/FormApplication";
// Dummy Component
import HtmlDesign from "./Views/Private/HtmlDesign";
import EmailTemplatePoc from "./Views/Private/FormBuilder/JSON/EmailTemplatePoc";
import FormDesignCOPY from "./Views/Private/FormBuilder/JSON/FormDesignCOPY";
import WorkflowDesign from "./Views/Private/FormBuilder/JSON/WorkflowDesign";
import EmailBuilderHTML from "./Views/Private/FormBuilder/JSON/EmailBuilderHTML";
import FormDesignHtmlComponent from "./Views/Private/FormBuilder/JSON/FormDesignHtmlComponent";
import WorkFlowPOC from "Views/Private/FormBuilder/JSON/NewWorkflow/WorkFlowPOC";
import DashboardWorkflow from "Views/Private/FormBuilder/JSON/DashboardWorkflow/DashboardWorkflow";
import PublishWorkflow from "Views/Private/FormBuilder/JSON/NewWorkflow/PublishWorkflow";
import Workflow from "Views/Private/FormBuilder/JSON/Workflow";
import { useCaptureUTM } from "Utils/hooks";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "react-toastify/dist/ReactToastify.css";
import "./Css/index.css?v=1.0.1";
import { AdminRules, CreateRule } from "Views/Private/Admin/AdminRules";

i18nInit();

function App() {
  const { formBuilder } = paths;

  useCaptureUTM();

  const renderLoader = () => <LoadingScreen />;
  return (
    <Suspense fallback={renderLoader()}>
      <ErrorBoundary>
        <AuthProvider>
          <HelmetProvider>
            <Routes>
              {/* Public Routes */}
              <Route
                element={
                  <PublicRoutes>
                    <PublicLayout />
                  </PublicRoutes>
                }
              >
                <Route index element={<Home />} />
                {/* This is your root / home */}
                <Route path={paths.dataSecurity} element={<DataSecurity />} />
                <Route path={paths.internshipManagementSystem} element={<InternshipManagementSystem />} />
                <Route path={paths.contactUs} element={<ContactUs />} />
                <Route path={paths.aboutUs} element={<AboutUs />} />
                <Route path={paths.privacyPolicy} element={<PrivacyPolicy />} />
                <Route path={paths.jobs} element={<Jobs />} />

                <Route path={paths.careers} element={<Careers />}>
                  <Route
                    index
                    element={<Navigate to={paths.careerJobsList} />}
                  />
                  <Route
                    path={paths.careerJobsList}
                    element={<CareerJobsList />}
                  />
                  <Route
                    path={`${paths.careerDetails}/:gid`}
                    element={<CareerDetails />}
                  />
                </Route>
              </Route>

              {/* Auth Routes */}
              <Route
                element={
                  <PublicRoutes>
                    <AuthLayout />
                  </PublicRoutes>
                }
              >
                <Route path={paths.login} element={<Login />} />
                <Route path={paths.registration} element={<CreateAccount />} />
                <Route path={paths.verifyEmail} element={<VerifyEmail />} />
                <Route
                  path={paths.forgetPassword}
                  element={<ForgetPassword />}
                />
                <Route path={paths.resetPassword} element={<ResetPassword />} />
              </Route>

              <Route
                path={`${paths.guestApplication}/:formId`}
                element={<GuestApplication />}
              />
              <Route
                path={`${paths.formInstruction}/:formId`}
                element={<FormInstruction />}
              />

              <Route
                path={paths.common.onboarding}
                element={
                  <PrivateRoutes>
                    <Onboarding />
                  </PrivateRoutes>
                }
              />

              {/* Private Route */}
              <Route
                element={
                  <PrivateRoutes>
                    <PrivateLayout />
                  </PrivateRoutes>
                }
              >
                <Route path="/auth/only-for-html" element={<HtmlDesign />} />
                <Route
                  path={paths.common.searchResult}
                  element={<SearchResult />}
                />
                <Route
                  path={paths.common.accountSetting}
                  element={<AccountSetting />}
                />
                <Route
                  path={paths.common.inviteMember}
                  element={<InviteMember />}
                />
                <Route path={paths.common.careerHub} element={<CareerHub />} />
                <Route path={paths.common.analytics} element={<Analytics />} />
                <Route
                  path={`${paths.common.viewProfile}/:userId?`}
                  element={<ViewProfile />}
                />
                <Route
                  path={paths.common.plutoConnect}
                  element={<PlutoConnect />}
                />

                {/* Admin */}
                <Route
                  path={paths.admin.dashboard}
                  element={<AdminDashboard />}
                />
                <Route
                  path={`${paths.admin.projects}/:projectid`}
                  element={<AdminProjects />}
                />
                <Route
                  path={`${paths.admin.applicationStatus.root}/:gid/*`}
                  element={<ApplicationStatus />}
                />
                <Route
                  path={`${paths.admin.applicationStatus.details}/:gid/*`}
                  element={<ApplicantDetails />}
                />
                <Route path={paths.admin.email} element={<AdminEmail />} />
                <Route path={paths.admin.events.root} element={<Events />} />
                <Route
                  path={paths.admin.events.create}
                  element={<CreateEvent />}
                />
                <Route
                  path={`${paths.admin.events.details}/:gid`}
                  element={<PreviewEvent />}
                />
                <Route
                  path={`${paths.admin.recommendation}/:menuId`}
                  element={<Recommendation />}
                />

                <Route
                  path={paths.admin.settings.root}
                  element={<AdminSettings />}
                >
                  <Route
                    index
                    element={<Navigate to={paths.admin.settings.stages} />}
                  />
                  <Route
                    path={paths.admin.settings.stages}
                    element={<Stages />}
                  />
                  <Route
                    path={paths.admin.settings.actions}
                    element={<Action />}
                  />
                  <Route
                    path={paths.admin.settings.field}
                    element={<Field />}
                  />
                  <Route
                    path={paths.admin.settings.attributes}
                    element={<Attributes />}
                  />
                  <Route
                    path={paths.admin.settings.roles}
                    element={<Roles />}
                  />
                  <Route
                    path={paths.admin.settings.groups}
                    element={<Groups />}
                  />
                </Route>

                <Route
                  path={`${paths.admin.settings.attributesDetails}/:id`}
                  element={<AttributeDetails />}
                />

                <Route
                  path={paths.admin.offerLetter.root}
                  element={<OfferLetter />}
                />
                <Route
                  path={paths.admin.offerLetter.create}
                  element={<NewOfferLetter />}
                />
                <Route
                  path={`${paths.admin.offerLetter.edit}/:gid`}
                  element={<NewOfferLetter />}
                />

                <Route
                  path={paths.admin.masterData.root}
                  element={<MasterData />}
                />
                <Route
                  path={`${paths.admin.masterData.details}/:gid`}
                  element={<MasterFieldDetails />}
                />

                <Route path={paths.admin.rules.root} element={<AdminRules />} />
                <Route
                  path={paths.admin.rules.create}
                  element={<CreateRule />}
                />

                {/* Applicant / Student */}
                <Route
                  path={paths.applicant.dashboard}
                  element={<StudentDashboard />}
                />
                <Route
                  path={paths.applicant.email}
                  element={<StudentEmail />}
                />

                {/* Form builder */}
                <Route
                  path={formBuilder.dashboard}
                  element={<FormBuilderDashboard />}
                />

                <Route
                  path={`${formBuilder.customizeForm.root}/:formId`}
                  element={<CustomizeForm />}
                >
                  <Route
                    path={formBuilder.customizeForm.formDesign}
                    element={<FormDesign />}
                  />
                  <Route
                    path={formBuilder.customizeForm.formSetting.root}
                    element={<FormSettings />}
                  />
                </Route>

                <Route
                  path={`${formBuilder.applicationForm}/:formId`}
                  element={<FormApplication />}
                />
                <Route path="/copy-form" element={<FormDesignCOPY />} />

                {/* POCs & utilities */}
                <Route
                  path="/auth/workflow/dashboard"
                  element={<DashboardWorkflow />}
                />
                <Route
                  path="/auth/workflow/dashboard/workflows/:workflowId"
                  element={<WorkFlowPOC />}
                />
                <Route
                  path="/auth/workflow/publish/:workflowId"
                  element={<PublishWorkflow />}
                />
                <Route path="/workflow-design" element={<WorkflowDesign />} />
                <Route
                  path="/email-builder-HTML"
                  element={<EmailBuilderHTML />}
                />
                <Route
                  path="/html-component"
                  element={<FormDesignHtmlComponent />}
                />
                <Route
                  path="/email-template-poc"
                  element={<EmailTemplatePoc />}
                />
                <Route path="/workflow" element={<Workflow />} />

                {/* Email Builder with its provider */}
                <Route
                  path={paths.admin.emailBuilder}
                  element={
                    <EmailBuilderProvider>
                      <EmailBuilder />
                    </EmailBuilderProvider>
                  }
                />
              </Route>
            </Routes>
            <ToastContainer
              theme="colored"
              position="bottom-right"
              autoClose={3000}
              hideProgressBar
              newestOnTop
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
            />
          </HelmetProvider>
        </AuthProvider>
      </ErrorBoundary>
    </Suspense>
  );
}

export default App;
